import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { fieldAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, MapPin, Banknote, Loader2, Building2, Camera, X, Image } from 'lucide-react';
import LocationPicker from '../components/LocationPicker';

export default function OwnerFields() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location: '',
    latitude: 0,
    longitude: 0,
    price_per_hour: '',
    amenities: '',
    photos: []
  });

  useEffect(() => {
    loadFields();
  }, []);

  const loadFields = async () => {
    try {
      const response = await fieldAPI.getMy();
      setFields(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Plätze');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate location is selected
    if (!formData.latitude || !formData.longitude || formData.latitude === 0 || formData.longitude === 0) {
      toast.error('Bitte wählen Sie einen Standort auf der Karte aus');
      return;
    }
    
    if (!formData.location.trim()) {
      toast.error('Bitte geben Sie eine Adresse ein');
      return;
    }
    
    const data = {
      ...formData,
      price_per_hour: parseFloat(formData.price_per_hour),
      amenities: formData.amenities.split(',').map(a => a.trim()).filter(Boolean),
      photos: formData.photos || []
    };

    try {
      if (editingField) {
        await fieldAPI.update(editingField.id, data);
        toast.success('Platz aktualisiert');
      } else {
        await fieldAPI.create(data);
        toast.success('Platz erstellt');
      }
      setDialogOpen(false);
      resetForm();
      loadFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    }
  };

  const handleEdit = (field) => {
    setEditingField(field);
    setFormData({
      name: field.name,
      description: field.description,
      location: field.location,
      latitude: field.latitude,
      longitude: field.longitude,
      price_per_hour: field.price_per_hour.toString(),
      amenities: field.amenities?.join(', ') || '',
      photos: field.photos || []
    });
    setDialogOpen(true);
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Möchten Sie diesen Platz wirklich löschen?')) return;
    
    try {
      await fieldAPI.delete(fieldId);
      toast.success('Platz gelöscht');
      loadFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Löschen');
    }
  };

  const resetForm = () => {
    setEditingField(null);
    setFormData({
      name: '',
      description: '',
      location: '',
      latitude: 0,
      longitude: 0,
      price_per_hour: '',
      amenities: '',
      photos: []
    });
  };

  const handleImageSelect = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte wähle ein Bild aus');
      return;
    }

    // Validate file size (max 500KB)
    if (file.size > 500 * 1024) {
      toast.error('Bild zu groß (max 500KB)');
      return;
    }

    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = event.target?.result;
        // Add to photos array (replace first photo or add new one)
        setFormData(prev => ({
          ...prev,
          photos: [base64]
        }));
        setUploadingImage(false);
        toast.success('Bild hinzugefügt');
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Fehler beim Verarbeiten des Bildes');
      setUploadingImage(false);
    }
    
    // Reset file input
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({
      ...prev,
      photos: []
    }));
    toast.success('Bild entfernt');
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-tunis-blue">Meine Plätze</h1>
            <p className="text-muted-foreground">{fields.length} Plätze registriert</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="w-4 h-4 mr-2" /> Neuen Platz hinzufügen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingField ? 'Platz bearbeiten' : 'Neuen Platz hinzufügen'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Name des Platzes *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="z.B. Sportplatz El Menzah"
                    required
                  />
                </div>
                <div>
                  <Label>Beschreibung *</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="Beschreiben Sie Ihren Platz..."
                    required
                  />
                </div>
                
                {/* Location Picker with Map */}
                <LocationPicker
                  latitude={formData.latitude}
                  longitude={formData.longitude}
                  address={formData.location}
                  onLocationChange={(lat, lng) => setFormData({...formData, latitude: lat, longitude: lng})}
                  onAddressChange={(addr) => setFormData({...formData, location: addr})}
                />
                
                <div>
                  <Label>Preis pro Stunde (TND) *</Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.price_per_hour}
                    onChange={(e) => setFormData({...formData, price_per_hour: e.target.value})}
                    placeholder="z.B. 50"
                    required
                  />
                </div>
                <div>
                  <Label>Ausstattung (kommagetrennt)</Label>
                  <Input
                    value={formData.amenities}
                    onChange={(e) => setFormData({...formData, amenities: e.target.value})}
                    placeholder="z.B. Flutlicht, Umkleide, Parkplatz"
                  />
                </div>
                
                {/* Image Upload */}
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Image className="w-4 h-4 text-tunis-teal" />
                    Platzbild / Logo
                  </Label>
                  
                  {formData.photos?.length > 0 ? (
                    <div className="relative w-full h-40 rounded-lg overflow-hidden border border-slate-200">
                      <img
                        src={formData.photos[0]}
                        alt="Platz-Bild"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={handleImageSelect}
                        >
                          <Camera className="w-4 h-4 mr-1" />
                          Ändern
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={handleRemoveImage}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Entfernen
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div
                      onClick={handleImageSelect}
                      className="w-full h-40 border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-tunis-teal hover:bg-slate-50 transition-colors"
                    >
                      {uploadingImage ? (
                        <Loader2 className="w-8 h-8 animate-spin text-tunis-teal" />
                      ) : (
                        <>
                          <Camera className="w-10 h-10 text-slate-400 mb-2" />
                          <span className="text-sm text-slate-500">Klicken zum Hochladen</span>
                          <span className="text-xs text-slate-400 mt-1">Max. 500KB, JPG/PNG</span>
                        </>
                      )}
                    </div>
                  )}
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    accept="image/*"
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    💡 Dieses Bild wird auf der Karte und in der Platzliste angezeigt.
                  </p>
                </div>
                
                <div className="flex gap-2 pt-4">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    Abbrechen
                  </Button>
                  <Button type="submit" className="flex-1 btn-primary">
                    {editingField ? 'Speichern' : 'Erstellen'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Fields Grid */}
        {fields.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {fields.map((field) => (
              <Card key={field.id} className="hover:shadow-lg transition-shadow overflow-hidden">
                {/* Field Image */}
                {field.photos?.length > 0 ? (
                  <div className="w-full h-40 overflow-hidden">
                    <img
                      src={field.photos[0]}
                      alt={field.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center">
                    <Building2 className="w-16 h-16 text-slate-300" />
                  </div>
                )}
                
                <CardContent className="pt-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-lg text-tunis-blue">{field.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {field.location?.substring(0, 30)}{field.location?.length > 30 ? '...' : ''}
                      </p>
                    </div>
                    <div className={`px-2 py-1 rounded text-xs font-medium ${field.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {field.is_active ? 'Aktiv' : 'Inaktiv'}
                    </div>
                  </div>
                  
                  <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{field.description}</p>
                  
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-1 text-tunis-teal">
                      <Banknote className="w-5 h-5" />
                      <span className="text-xl font-bold">{field.price_per_hour}</span>
                      <span className="text-sm text-muted-foreground">TND/Std.</span>
                    </div>
                  </div>
                  
                  {field.amenities?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {field.amenities.slice(0, 3).map((amenity, i) => (
                        <span key={i} className="px-2 py-1 bg-gray-100 rounded text-xs">{amenity}</span>
                      ))}
                      {field.amenities.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 rounded text-xs">+{field.amenities.length - 3}</span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEdit(field)}>
                      <Edit className="w-4 h-4 mr-1" /> Bearbeiten
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(field.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="py-16 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Keine Plätze vorhanden</h3>
              <p className="text-muted-foreground mb-4">Fügen Sie Ihren ersten Fußballplatz hinzu</p>
              <Button className="btn-primary" onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Ersten Platz hinzufügen
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
